import { CHART, SERIES } from '@/lib/chartColors';
import React, { Suspense, lazy, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Table, THead, TBody, Tr, Th, Td, TableSkeleton } from '@/components/ui/Table';
import { fieldControlSm, fieldLabel, fieldTextarea } from '@/components/ui/fieldStyles';
import { ChartTooltip } from '@/components/charts/ChartTooltip';
import {
  trafficApi, hourOfWeekLabel,
  type CollectionState, type TrafficFlag, type TrafficSummary, type TrafficRoadDetail,
} from '@/lib/trafficApi';

const TrafficCellMap = lazy(() => import('@/components/traffic/TrafficCellMap'));

const axis = { tickLine: false, axisLine: false } as const;
const kph = (n: number) => `${n} km/h`;
const LAGOS: [number, number] = [3.39, 6.52];

const STATES: { id: CollectionState; label: string; confirm: string; explain: string }[] = [
  { id: 'off', label: 'Off', confirm: 'Switch off', explain: 'The app stops collecting and the gateway drops any batch it receives.' },
  { id: 'dry_run', label: 'Dry run', confirm: 'Switch to dry run', explain: 'The whole pipeline runs and is checked, but nothing is stored. Use it to test, then switch off.' },
  { id: 'on', label: 'On', confirm: 'Switch on', explain: 'Navigating app users start sending anonymous speed data. Only switch on once the notice wording has passed legal review.' },
];

const REASONS: Record<string, { label: string; color: string }> = {
  // One accent for what was kept; tints and neutrals for the reasons a batch was turned away.
  accepted: { label: 'Accepted', color: CHART.primary },
  rate: { label: 'Rate limited', color: SERIES[1]! },
  format: { label: 'Bad format', color: SERIES[3]! },
  too_fast: { label: 'Too fast for mode', color: SERIES[4]! },
  not_a_path: { label: 'Not a path', color: SERIES[2]! },
  engine_unavailable: { label: 'Engine unavailable', color: SERIES[5]! },
};

const CHECKS: Record<string, string> = {
  impossible_speed: 'Impossible speed',
  stuck: 'Stuck (mostly stopped)',
  sudden_change: 'Sudden change',
};

function flagDetail(f: TrafficFlag): string {
  const d = f.detail ?? {};
  if (f.check_name === 'impossible_speed') return `${d.kph} km/h; city typical ${d.cityMedian} km/h at ${hourOfWeekLabel(d.hourOfWeek ?? 0)}`;
  if (f.check_name === 'stuck') return `${Math.round((d.dwellShare ?? 0) * 100)}% of segments stopped`;
  if (f.check_name === 'sudden_change') return `${d.kph} km/h vs ${d.lastWeekKph} km/h a week earlier at ${hourOfWeekLabel(d.hourOfWeek ?? 0)}`;
  return JSON.stringify(d);
}

const when = (s: number | null) => (s ? new Date(s * 1000).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** A small chart of speed through the week, shared by the cell and road details. */
const WeekSpeedChart: React.FC<{ data: (number | null)[]; name: string }> = ({ data, name }) => (
  <div className="h-56">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data.map((v, h) => ({ h: hourOfWeekLabel(h), kph: v }))} margin={{ top: 8, right: 4, bottom: 0, left: -8 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="h" interval={23} {...axis} />
        <YAxis unit=" km/h" width={64} {...axis} />
        <Tooltip content={<ChartTooltip format={kph} />} cursor={{ stroke: 'rgb(var(--color-border))' }} />
        <Line type="monotone" dataKey="kph" stroke={CHART.primary} strokeWidth={2} dot={false} connectNulls={false} name={name} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const ModalLoading = () => <div aria-hidden="true" className="space-y-3"><div className="skeleton h-56" /><SkeletonLines count={3} lineClassName="h-8" /></div>;

const TrafficDataPage: React.FC = () => {
  const qc = useQueryClient();
  const summary = useQuery<TrafficSummary>({ queryKey: ['traffic-summary'], queryFn: () => trafficApi.summary(14) });
  const flags = useQuery<TrafficFlag[]>({ queryKey: ['traffic-flags'], queryFn: () => trafficApi.flags('open') });
  const exclusions = useQuery({ queryKey: ['traffic-exclusions'], queryFn: () => trafficApi.exclusions() });

  const [pendingState, setPendingState] = useState<CollectionState | null>(null);
  const [excluding, setExcluding] = useState<TrafficFlag | null>(null);
  const [reason, setReason] = useState('');
  const [how, setHow] = useState<number | null>(null);
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null);
  const [openCell, setOpenCell] = useState<string | null>(null);
  const [openRoad, setOpenRoad] = useState<{ edge: number; piece: number } | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['traffic-summary'] });
    void qc.invalidateQueries({ queryKey: ['traffic-flags'] });
    void qc.invalidateQueries({ queryKey: ['traffic-exclusions'] });
  };

  const setSwitch = useMutation({
    mutationFn: (s: CollectionState) => trafficApi.setSwitch(s),
    onSuccess: () => { setPendingState(null); toast.success('Collection switch updated'); refresh(); },
    onError: () => toast.error('Could not change the switch'),
  });
  const clear = useMutation({
    mutationFn: (id: string) => trafficApi.clearFlag(id),
    onSuccess: () => { toast.success('Flag cleared; the cell counts again'); refresh(); },
    onError: () => toast.error('Could not clear the flag'),
  });
  const exclude = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => trafficApi.excludeFlag(id, why),
    onSuccess: () => { setExcluding(null); setReason(''); toast.success('Cell excluded'); refresh(); },
    onError: () => toast.error('Could not exclude the cell'),
  });
  const removeExclusion = useMutation({
    mutationFn: (id: string) => trafficApi.removeExclusion(id),
    onSuccess: () => { toast.success('Exclusion removed'); refresh(); },
  });

  const cells = useQuery({
    queryKey: ['traffic-cells', bbox, how],
    queryFn: () => trafficApi.cells(bbox!, 8, how),
    enabled: !!bbox,
  });
  const roads = useQuery({
    queryKey: ['traffic-roads', bbox, how],
    queryFn: () => trafficApi.roads(bbox!, how),
    enabled: !!bbox,
  });
  const roadDetail = useQuery<TrafficRoadDetail>({
    queryKey: ['traffic-road', openRoad?.edge],
    queryFn: () => trafficApi.road(openRoad!.edge),
    enabled: !!openRoad,
  });
  const cellDetail = useQuery({
    queryKey: ['traffic-cell', openCell],
    queryFn: () => trafficApi.cell(8, openCell!),
    enabled: !!openCell,
  });

  const s = summary.data;
  const latest = s?.reports[0];
  const intakeChart = useMemo(() => (s?.days ?? []).map(d => ({
    day: d.day.slice(5),
    ...Object.fromEntries(Object.entries(d.byReason).map(([k, v]) => [k, v.batches])),
  })), [s]);
  const matching = (s?.matching ?? []).reduce((a, d) => ({
    matched: a.matched + d.matched, unmatched: a.unmatched + d.unmatched, failed: a.failed + d.failed, skipped: a.skipped + d.skipped,
  }), { matched: 0, unmatched: 0, failed: 0, skipped: 0 });
  const matchTotal = matching.matched + matching.unmatched + matching.failed;
  const roadPiece = roadDetail.data?.pieces.find(p => p.piece === openRoad?.piece) ?? roadDetail.data?.pieces[0];
  const cities = Object.entries(latest?.report.cities ?? {}).sort((a, b) => b[1].batches - a[1].batches);
  const pending = STATES.find(x => x.id === pendingState);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Traffic data"
        description="Anonymous speeds from navigating app users, checked nightly. Visible to admins only."
      />

      {(summary.isError || flags.isError) && (
        <Notice tone="error">Could not load traffic data. The speed engine may be unreachable; try again shortly.</Notice>
      )}

      <Panel title="Collection">
        <div role="radiogroup" aria-label="Collection switch" className="inline-flex items-center rounded-full border border-border bg-surface p-0.5">
          {STATES.map(x => {
            const active = s?.switch.state === x.id;
            return (
              <button key={x.id} type="button" role="radio" aria-checked={active} disabled={!s}
                onClick={() => { if (!active) setPendingState(x.id); }}
                className={`h-8 rounded-full px-3.5 text-[13px] font-medium transition-colors disabled:opacity-50 ${active ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}>
                {x.label}
              </button>
            );
          })}
        </div>
        {s ? (
          <p className="mt-3 max-w-[70ch] text-sm text-muted-foreground">
            {STATES.find(x => x.id === s.switch.state)?.explain}
            {s.switch.changedBy && <> Last changed by {s.switch.changedBy} on {when(s.switch.changedAt)}.</>}
          </p>
        ) : <div className="skeleton mt-3 h-4 w-80 max-w-full" aria-hidden="true" />}
      </Panel>

      <Panel title="Intake, last 14 days (batches)">
        {summary.isLoading ? <div className="skeleton h-64" aria-hidden="true" />
          : intakeChart.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={intakeChart} margin={{ top: 8, right: 0, bottom: 0, left: -16 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="day" {...axis} />
                  <YAxis allowDecimals={false} width={44} {...axis} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgb(var(--color-surface-raised))' }} />
                  <Legend iconType="circle" iconSize={8} />
                  {Object.entries(REASONS).map(([k, r]) => <Bar key={k} dataKey={k} name={r.label} stackId="a" fill={r.color} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState title={`No data yet${s?.switch.state === 'off' ? ': collection is off.' : '.'}`} className="rounded-xl border border-dashed border-border" />}
        {s && (
          <p className="mt-4 text-sm text-muted-foreground">
            Road matching: {matchTotal
              ? <>{matching.matched} of {matchTotal} car, keke and motorbike segments matched to a road ({Math.round((matching.matched / matchTotal) * 100)}%)
                  {matching.unmatched ? `, ${matching.unmatched} with no plausible road` : ''}{matching.failed ? `, ${matching.failed} not matched because routing was unreachable` : ''}.</>
              : 'no segments matched yet.'}
            {matching.skipped ? ` ${matching.skipped} bike and walking segments count towards cells only.` : ''}
          </p>
        )}
      </Panel>

      <Panel title={`Coverage by city${latest ? ` (${latest.day}${latest.status !== 'ok' ? `, ${latest.status}` : ''})` : ''}`} bodyClassName="px-0 pb-2">
        {summary.isLoading ? <TableSkeleton rows={3} />
          : cities.length ? (
            <Table minWidth={480}>
              <THead>
                <tr><Th>City</Th><Th align="right">Batches</Th><Th align="right">Usable cells</Th><Th align="right">Withheld cells</Th></tr>
              </THead>
              <TBody>
                {cities.map(([name, c]) => (
                  <Tr key={name}>
                    <Td>{name}</Td><Td align="right" mono>{c.batches}</Td><Td align="right" mono>{c.usable}</Td><Td align="right" mono muted>{c.withheld}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          ) : <p className="px-5 pb-4 pt-1 text-sm text-muted-foreground">No city report yet. The first nightly check runs at 04:30 Lagos time after data arrives.</p>}
      </Panel>

      <Panel title="Open flags" bodyClassName="px-0 pb-2">
        {flags.isLoading ? <TableSkeleton rows={2} />
          : flags.data?.length ? (
            <ul className="divide-y divide-border">
              {flags.data.map(f => (
                <li key={f.id} data-testid={`flag-${f.id}`} className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{CHECKS[f.check_name] ?? f.check_name}</p>
                    <p className="text-sm text-muted-foreground">{flagDetail(f)}</p>
                    <button type="button" className="mt-0.5 font-mono text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" onClick={() => setOpenCell(f.cell)}>Cell {f.cell}</button>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" loading={clear.isLoading && clear.variables === f.id} onClick={() => clear.mutate(f.id)}>Clear</Button>
                    <Button size="sm" variant="danger" onClick={() => { setExcluding(f); setReason(''); }}>Exclude</Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : <p className="px-5 pb-4 pt-1 text-sm text-muted-foreground">No open flags. Cells the nightly checks question appear here.</p>}
      </Panel>

      <Panel
        title="Usable roads and cells"
        action={
          <div className="flex items-center gap-2">
            <label htmlFor="how" className="whitespace-nowrap text-xs text-muted-foreground">Time of week</label>
            <select id="how" className={`${fieldControlSm} w-auto`}
              value={how ?? ''} onChange={e => setHow(e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">Typical (all week)</option>
              {Array.from({ length: 168 }, (_, h) => <option key={h} value={h}>{hourOfWeekLabel(h)}</option>)}
            </select>
          </div>
        }
      >
        {roads.data && cells.data && (
          <p className="mb-3 text-xs text-muted-foreground">
            <span className="font-mono tabular-nums text-foreground">{roads.data.length}</span> road pieces and <span className="font-mono tabular-nums text-foreground">{cells.data.length}</span> cells in view
          </p>
        )}
        <Suspense fallback={<div className="skeleton h-[28rem] rounded-2xl" aria-hidden="true" />}>
          <TrafficCellMap cells={cells.data ?? []} roads={roads.data ?? []} center={LAGOS} zoom={11} onMove={setBbox}
            onCellClick={setOpenCell} onRoadClick={(edge, piece) => setOpenRoad({ edge, piece })} />
        </Suspense>
        <p className="mt-3 max-w-[80ch] text-xs text-muted-foreground">
          Lines are the roads people actually drove, in pieces of about 100 m, one line per direction. Red is well below the road's normal speed, green at or near it.
          The faint hexagons show the average for the whole area. Roads and cells with fewer than 5 batches, or in an excluded or flagged cell, are never shown.
        </p>
      </Panel>

      <Panel title="Exclusions" bodyClassName="px-0 pb-2">
        {exclusions.isLoading ? <TableSkeleton rows={2} />
          : exclusions.data?.length ? (
            <ul className="divide-y divide-border">
              {exclusions.data.map(x => (
                <li key={x.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-foreground">{x.reason}</p>
                    <p className="text-muted-foreground"><span className="font-mono text-xs">Cell {x.cell}</span> · by {x.admin} on {when(x.created_at)}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => removeExclusion.mutate(x.id)}>Remove</Button>
                </li>
              ))}
            </ul>
          ) : <p className="px-5 pb-4 pt-1 text-sm text-muted-foreground">No exclusions. Cells you exclude from a flag are listed here.</p>}
      </Panel>

      <Modal isOpen={!!pending} onClose={() => setPendingState(null)} title={pending?.confirm}>
        <p className="text-sm text-muted-foreground">{pending?.explain}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPendingState(null)}>Cancel</Button>
          <Button variant={pending?.id === 'on' ? 'danger' : 'primary'} loading={setSwitch.isLoading}
            onClick={() => pending && setSwitch.mutate(pending.id)}>{pending?.confirm}</Button>
        </div>
      </Modal>

      <Modal isOpen={!!excluding} onClose={() => setExcluding(null)} title="Exclude this cell">
        <p className="text-sm text-muted-foreground">
          {excluding ? `${CHECKS[excluding.check_name] ?? excluding.check_name}: ${flagDetail(excluding)}.` : ''} Its data will no longer count.
        </p>
        <label htmlFor="exclude-reason" className={`${fieldLabel} mt-4`}>Reason</label>
        <textarea id="exclude-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)} className={fieldTextarea} />
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setExcluding(null)}>Cancel</Button>
          <Button variant="danger" disabled={!reason.trim()} loading={exclude.isLoading}
            onClick={() => excluding && exclude.mutate({ id: excluding.id, why: reason.trim() })}>Exclude cell</Button>
        </div>
      </Modal>

      <Modal isOpen={!!openCell} onClose={() => setOpenCell(null)} title={openCell ? `Cell ${openCell}` : ''} size="lg">
        {cellDetail.isLoading ? <ModalLoading />
          : cellDetail.isError ? <p className="text-sm text-muted-foreground">This cell has no usable data (fewer than 5 batches, excluded or flagged).</p>
          : cellDetail.data ? (
            <div className="space-y-4">
              <WeekSpeedChart data={cellDetail.data.profileHourly} name="Typical speed" />
              <div className="-mx-6 border-t border-border">
                <Table minWidth={360}>
                  <THead><tr><Th>Week of</Th><Th align="right">Speed</Th><Th align="right">Batches</Th></tr></THead>
                  <TBody>
                    {cellDetail.data.weeks.map(w => (
                      <Tr key={w.weekStart}>
                        <Td>{new Date(w.weekStart * 1000).toLocaleDateString('en-GB', { dateStyle: 'medium' })}</Td>
                        <Td align="right" mono>{w.kph == null ? 'no data' : `${w.kph} km/h`}</Td><Td align="right" mono muted>{w.batches}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          ) : null}
      </Modal>

      <Modal isOpen={!!openRoad} onClose={() => setOpenRoad(null)} title={roadDetail.data?.name ?? 'Unnamed road'} size="lg">
        {roadDetail.isLoading ? <ModalLoading />
          : roadDetail.isError ? <p className="text-sm text-muted-foreground">This road has no usable data (fewer than 5 batches, or in an excluded or flagged cell).</p>
          : roadDetail.data && roadPiece ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {roadDetail.data.roadClass?.replace(/_/g, ' ') ?? 'Road'} · {roadDetail.data.lengthM} m in this direction
                {roadDetail.data.refKph ? ` · normal speed ${roadDetail.data.refKph} km/h` : ''} · OSM way {roadDetail.data.wayId}
              </p>
              <div>
                <p className="mb-2 text-sm font-medium text-foreground">
                  Piece {roadPiece.piece + 1} (about 100 m each, in travel order): typical {roadPiece.typicalKph} km/h,
                  stopped {Math.round(roadPiece.stoppedShare * 100)}% of the time, {roadPiece.batches} batches
                </p>
                <WeekSpeedChart data={roadPiece.hourly} name="Speed" />
                <p className="mt-1 text-xs text-muted-foreground">Hours with fewer than 5 batches are left blank.</p>
              </div>
              {roadDetail.data.pieces.length > 1 && (
                <div className="-mx-6 border-t border-border">
                  <Table minWidth={400}>
                    <THead><tr><Th>Piece</Th><Th align="right">Typical</Th><Th align="right">Stopped</Th><Th align="right">Batches</Th></tr></THead>
                    <TBody>
                      {roadDetail.data.pieces.map(p => (
                        <Tr key={p.piece} className={p.piece === roadPiece.piece ? 'bg-surface-raised/60' : undefined}>
                          <Td mono>
                            <button type="button" className={`underline-offset-2 hover:underline ${p.piece === roadPiece.piece ? 'font-medium text-accent-text' : ''}`} onClick={() => setOpenRoad({ edge: roadDetail.data!.edge, piece: p.piece })}>
                              {p.piece + 1}
                            </button>
                          </Td>
                          <Td align="right" mono>{p.typicalKph} km/h</Td><Td align="right" mono muted>{Math.round(p.stoppedShare * 100)}%</Td><Td align="right" mono muted>{p.batches}</Td>
                        </Tr>
                      ))}
                    </TBody>
                  </Table>
                </div>
              )}
              <div className="-mx-6 border-t border-border">
                <Table minWidth={360}>
                  <THead><tr><Th>Week of</Th><Th align="right">Speed</Th><Th align="right">Batches</Th></tr></THead>
                  <TBody>
                    {roadDetail.data.weeks.map(w => (
                      <Tr key={w.weekStart}>
                        <Td>{new Date(w.weekStart * 1000).toLocaleDateString('en-GB', { dateStyle: 'medium' })}</Td>
                        <Td align="right" mono>{w.kph == null ? (w.batches ? 'too few batches' : 'no data') : `${w.kph} km/h`}</Td><Td align="right" mono muted>{w.batches}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          ) : null}
      </Modal>
    </div>
  );
};

export default TrafficDataPage;
